// Shared color-application logic used by:
// - The EditorJS `ColorTool` (`editorjs-tools/color/color-tool.ts`) inside
//   paragraph clusters.
// - The custom selection toolbar (`hooks/use-selection-toolbar.tsx`) used
//   on container titles via `EditableText`.
// Both code paths must produce the SAME DOM output (`<span style="color:#…">…</span>`)
// so a paragraph copy/pasted into a title (or vice versa) stays consistent
// across the sanitizer + re-render round trip.

import {
  NOTE_INLINE_COLOR,
  TEXT_STYLE_INLINE_COLORS,
} from '@worldathletics/utilities';

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export const DEFAULT_COLOR_PALETTE: readonly string[] = [
  '#000000',
  NOTE_INLINE_COLOR,
  TEXT_STYLE_INLINE_COLORS.exception,
  '#dc3545',
  '#007bff',
  '#6c757d',
];

// Module-level fallback: the most recent color the user picked. Used as the
// swatch background when the current selection isn't itself inside a color
// span — so the toolbar button visibly reflects "what you most recently
// applied" instead of staying frozen on the green default forever.
let lastPickedColor: string | null = null;

export function getLastPickedColor(): string | null {
  return lastPickedColor;
}

export function setLastPickedColor(color: string | null): void {
  if (color === null) {
    lastPickedColor = null;
    return;
  }
  if (!COLOR_RE.test(color)) return;
  lastPickedColor = color.toLowerCase();
}

/**
 * Wrap `range` in `<span style="color:#rrggbb">…</span>`.
 * Returns `false` if the color is not a `#RRGGBB` literal or if the DOM
 * mutation throws (invalid range). On success the range's contents are
 * replaced by the wrapper span, the wrapper's contents become the live
 * selection (so a caller re-reading `window.getSelection()` immediately
 * after this call gets a fresh, non-collapsed range instead of the
 * collapsed range left behind by `extractContents`), and `lastPickedColor`
 * is updated.
 *
 * If `range` exactly spans the full contents of an existing lone color
 * span, that span's style is mutated in place instead of wrapping a new
 * span around it — avoids nesting spans when the user picks a different
 * color repeatedly in the same session (this is exactly what happens on the
 * 2nd+ pick, since the 1st pick's reselect above leaves the live selection
 * as precisely that span's contents).
 */
export function applyColorToRange(range: Range, color: string): boolean {
  if (!COLOR_RE.test(color)) return false;
  const normalised = color.toLowerCase();
  const existing = findExactWrappingColorSpan(range);
  if (existing) {
    existing.setAttribute('style', `color:${normalised}`);
    setLastPickedColor(normalised);
    dispatchInputEventOnEditableHost(existing);
    reselectElementContents(existing);
    return true;
  }
  const span = document.createElement('span');
  span.setAttribute('style', `color:${normalised}`);
  try {
    span.appendChild(range.extractContents());
    range.insertNode(span);
  } catch {
    // extractContents or insertNode can throw if the range is invalid.
    return false;
  }
  setLastPickedColor(normalised);
  dispatchInputEventOnEditableHost(span);
  reselectElementContents(span);
  return true;
}

/**
 * Returns the nearest ancestor SPAN of `range.startContainer` when it is a
 * lone color span (per `getColorFromSpan`) AND `range` exactly spans that
 * span's full contents — i.e. the selection IS that span, not merely a
 * sub-range or overlapping range. Used to replace a color in place rather
 * than nesting a new span around an existing one.
 */
function findExactWrappingColorSpan(range: Range): HTMLElement | null {
  if (range.collapsed) return null;
  const startEl =
    range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : (range.startContainer as HTMLElement | null);
  if (!startEl || getColorFromSpan(startEl) === null) return null;
  const spanRange = document.createRange();
  spanRange.selectNodeContents(startEl);
  const startsMatch =
    range.compareBoundaryPoints(Range.START_TO_START, spanRange) === 0;
  const endsMatch =
    range.compareBoundaryPoints(Range.END_TO_END, spanRange) === 0;
  return startsMatch && endsMatch ? startEl : null;
}

/**
 * Replace the live browser selection with one spanning `el`'s contents.
 * Called after a DOM mutation so a caller re-reading `window.getSelection()`
 * immediately afterward gets a fresh, correct range — `Range#extractContents`
 * collapses whatever range it's called on, so a cached pre-mutation Range
 * reference is useless for a subsequent apply/remove in the same session.
 */
function reselectElementContents(el: HTMLElement): void {
  const sel = window.getSelection();
  if (!sel) return;
  const newRange = document.createRange();
  newRange.selectNodeContents(el);
  sel.removeAllRanges();
  sel.addRange(newRange);
}

function endOffsetForNode(node: Node): number {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent?.length ?? 0;
  }
  return node.childNodes.length;
}

/** Reselect a contiguous run of sibling nodes (e.g. unwrapped span contents). */
export function reselectBetweenNodes(startNode: Node, endNode: Node): void {
  const sel = window.getSelection();
  if (!sel) return;
  const newRange = document.createRange();
  newRange.setStart(startNode, 0);
  newRange.setEnd(endNode, endOffsetForNode(endNode));
  sel.removeAllRanges();
  sel.addRange(newRange);
}

export function isCaretInsideColoredSpan(): boolean {
  return findColorAncestorOfSelection() !== null;
}

/**
 * Read a color from a span's INLINE style. Uses `el.style.color` rather
 * than `getAttribute('style')` because browsers re-emit `color:#28a745` as
 * `rgb(40, 167, 69)` via CSSStyleDeclaration normalisation, so a regex on
 * the raw attribute string is fragile across engines.
 *
 * Returns canonical `#rrggbb` (lower-case) or `null` if the span:
 *   - has no inline color, OR
 *   - has *additional* inline styles (security: don't preserve unknown
 *     CSS — a paste could carry e.g. `background:url(javascript:…)` next
 *     to a benign color).
 */
export function getColorFromSpan(el: HTMLElement): string | null {
  if (el.tagName !== 'SPAN') return null;
  const colorValue = el.style.color;
  if (!colorValue) return null;
  // Reject spans that carry more than one declared property — we only round-
  // trip the pure single-property color case.
  if (el.style.length !== 1 || el.style.item(0) !== 'color') return null;
  return normaliseToHex(colorValue);
}

/**
 * Walk up from `node` looking for the nearest SPAN ancestor whose only
 * inline style is `color`. Returns that span or null. Excludes `.ref-chip`
 * because chip spans use the same tag but are semantically references.
 */
function findColorAncestor(node: Node | null): HTMLElement | null {
  let cur: Node | null = node;
  while (cur) {
    if (cur.nodeType === Node.ELEMENT_NODE) {
      const el = cur as HTMLElement;
      if (el.tagName === 'SPAN' && !el.classList.contains('ref-chip')) {
        const colorHex = getColorFromSpan(el);
        if (colorHex) return el;
      }
    }
    cur = cur.parentNode;
  }
  return null;
}

function findColorAncestorOfSelection(): HTMLElement | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  return findColorAncestor(sel.getRangeAt(0).startContainer);
}

/**
 * Color currently applied at the selection start — for swatch display.
 * Returns null when the caret isn't inside a colored span; callers may
 * fall through to `getLastPickedColor()` for the visual "active" hint.
 */
export function getSelectionColor(): string | null {
  const ancestor = findColorAncestorOfSelection();
  if (!ancestor) return null;
  return getColorFromSpan(ancestor);
}

/**
 * Strip color formatting around `range` by unwrapping the nearest
 * colored-span ancestor of its start container. v1 limitation: removes
 * color from the ENTIRE span's contents, not just the selected sub-range —
 * matches Word/Google Docs reset-color behaviour. Returns `true` if a
 * span was unwrapped.
 *
 * Takes a Range explicitly (rather than reading `window.getSelection()`)
 * so callers can capture the user's intended range at click-time and not
 * race with focus/selection clearing that the click event may have
 * triggered in the meantime.
 */
export function removeColorFromRange(range: Range): boolean {
  const ancestor = findColorAncestor(range.startContainer);
  if (!ancestor) return false;
  const firstChild = ancestor.firstChild;
  const lastChild = ancestor.lastChild;
  const ok = unwrapElement(ancestor);
  if (!ok) return false;
  if (firstChild) {
    dispatchInputEventOnEditableHost(firstChild);
    reselectBetweenNodes(firstChild, lastChild ?? firstChild);
  }
  return true;
}

/** @deprecated Prefer the range-based form. Kept for backwards compatibility. */
export function removeColorFromSelection(): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  return removeColorFromRange(sel.getRangeAt(0));
}

function unwrapElement(el: HTMLElement): boolean {
  const parent = el.parentNode;
  if (!parent) return false;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
  return true;
}

/**
 * Notify any contenteditable host of the mutation we just made via raw Range
 * APIs. Without this dispatch, the host's `onInput` JSX handler never runs,
 * so `EditableText.handleInput` never serializes the new DOM into Redux and
 * `setIsDirty(true)` never fires — making the "Save Draft" button look like
 * "nothing changed" to the user.
 *
 * We walk up from `start` looking for the FIRST ancestor whose
 * `isContentEditable === true`. Dispatching on `document` would trigger
 * unrelated input listeners; scoping to the host is correct.
 */
export function dispatchInputEventOnEditableHost(start: Node): void {
  let cur: Node | null = start;
  while (cur) {
    if (cur.nodeType === Node.ELEMENT_NODE) {
      const el = cur as HTMLElement;
      // Use BOTH the attribute and the inherited-getter forms.
      // `isContentEditable` follows the inheritance chain in real browsers,
      // but jsdom's coverage is thin — testing the attribute directly
      // catches hosts created via `setAttribute('contenteditable', 'true')`
      // that jsdom's getter might miss.
      const attr = el.getAttribute('contenteditable');
      const editable =
        el.isContentEditable || attr === 'true' || attr === '';
      if (editable) {
        el.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
    }
    cur = cur.parentNode;
  }
}

/**
 * Normalise a browser `style.color` value (commonly `rgb(r, g, b)`,
 * sometimes a `#hex`, occasionally a colour name) to `#rrggbb`. Returns
 * `null` if it can't be expressed as 6-digit hex (e.g. alpha-bearing rgba).
 */
function normaliseToHex(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  if (COLOR_RE.test(trimmed)) return trimmed;
  const shortHex = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(trimmed);
  if (shortHex) {
    return `#${shortHex[1].repeat(2)}${shortHex[2].repeat(2)}${shortHex[3].repeat(2)}`;
  }
  const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(
    trimmed,
  );
  if (rgb) {
    const toHex = (n: string): string =>
      Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0');
    return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`;
  }
  return null;
}

// Exported for tests so we can assert browser-normalisation handling
// without re-instantiating the whole tool.
export const __test = { normaliseToHex };
